import {
  Card,
  CardContent,
  Icon,
  type IconGlyph,
  Skeleton,
  Stack,
} from '@hurdl/design-system';
import { type ReactElement, type ReactNode } from 'react';

/**
 * The one tile the panel's boards are built from, so every box — a Product on the
 * dashboard, a Restaurant in the list, a destination on a Restaurant's hub — is laid
 * out the same way and stays aligned however the content differs.
 *
 * ── Why one component ────────────────────────────────────────────────────────
 *
 * The dashboard, the Restaurants list and the Restaurant hub each grew their own card
 * with its own header, its own count treatment and its own hover — so they drifted, and
 * one read differently from the next. These are the same object at heart (an icon, a
 * title, a line of description, a figure or two, and somewhere to go), so they are one
 * component now: a change to the tile reaches every board at once, and a new board
 * cannot invent a fifth card shape by accident.
 *
 * ── The pieces ───────────────────────────────────────────────────────────────
 *
 * {@link Tile} is the glass surface and the vertical rhythm. {@link TileHead} is the
 * icon chip, title and description every tile leads with. {@link TileStat} is the one
 * way a count is shown — a figure at the display face and its noun beside it, never a
 * number stuffed in a badge next to a repeated noun, the treatment this component exists
 * to abolish — and {@link TileStats} rows a few on one inset panel.
 *
 * A tile that is *itself* a destination wraps a {@link Tile} with `interactive` in the
 * page's own typed router `Link` (so the concrete route stays the page's, and this
 * module needs no router types): the `Link` carries {@link TILE_LINK_CLASS} and the Tile
 * lifts a hair on hover. A tile that holds its own actions (the dashboard Product card)
 * is a plain {@link Tile} with buttons in its footer.
 */

/**
 * The classes a router `Link` wrapping an interactive {@link Tile} carries: a block with
 * a focus ring and the `group` the Tile's hover lift keys off. A page composes it onto
 * its own typed `Link`, keeping the route literal in the page.
 */
export const TILE_LINK_CLASS =
  'group block rounded-hurdl-large outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * The glass tile surface and its layout.
 *
 * `interactive` adds the hover lift (keyed to the platform motion, held still under
 * reduced motion) and expects a {@link TILE_LINK_CLASS} `Link` as the parent, whose
 * `group` hover drives the lift. A static tile omits it — a whole-card motion on a panel
 * that is not a link would be noise.
 */
export function Tile({
  children,
  interactive = false,
}: {
  readonly children: ReactNode;
  readonly interactive?: boolean;
}): ReactElement {
  const lift = interactive
    ? ' transition-transform duration-(--duration-hurdl-medium) ease-(--ease-hurdl-spring) group-hover:-translate-y-hurdl-1 motion-reduce:transition-none motion-reduce:group-hover:translate-y-0'
    : '';

  return (
    <Card className={`panel-glass h-full gap-hurdl-5${lift}`}>
      <CardContent>
        <Stack gap="large">{children}</Stack>
      </CardContent>
    </Card>
  );
}

/**
 * The head every tile leads with: an icon chip (a muted inset square that anchors the
 * tile and gives it a face), the title at the display face, and an optional line of
 * description. The text truncates rather than widening the tile.
 */
export function TileHead({
  icon,
  title,
  description,
}: {
  readonly icon: IconGlyph;
  readonly title: string;
  readonly description?: string;
}): ReactElement {
  return (
    <Stack direction="horizontal" gap="medium" align="centre">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-hurdl-medium bg-muted text-card-foreground">
        <Icon icon={icon} />
      </span>
      <Stack gap="small" className="min-w-0">
        <h2 className="min-w-0 truncate font-hurdl-display text-hurdl-subheading font-hurdl-semibold">
          {title}
        </h2>
        {description !== undefined ? (
          <p className="text-hurdl-caption text-hurdl-muted-foreground">
            {description}
          </p>
        ) : undefined}
      </Stack>
    </Stack>
  );
}

/**
 * The inset stat panel every tile carries — the same recessed, bordered box on every
 * board, so a Product tile, a Restaurant tile and a hub tile present their figures
 * identically rather than one having a nice box and the next a bare number. It holds one
 * or more {@link TileStat}s, spread along the row.
 */
export function TileStats({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  return (
    <div className="flex items-stretch justify-between gap-hurdl-6 rounded-hurdl-medium border border-border bg-muted/70 px-hurdl-4 py-hurdl-3">
      {children}
    </div>
  );
}

/**
 * The one way a figure is shown on a tile: a small uppercase label above a figure at the
 * display face. No noun stuck beside the number and no badge — the label is the noun
 * ("TO REVIEW", "PUBLISHED", "MANAGED"), so the figure stays clean and every tile's stat
 * reads the same. `emphasis` colours the figure in the brand accent (for the number that
 * is the point of the tile, e.g. work waiting); otherwise it takes the card's own text.
 * `undefined` value renders a skeleton, so a tile loads without jumping.
 */
export function TileStat({
  label,
  value,
  emphasis = false,
}: {
  readonly label: string;
  readonly value: number | undefined;
  readonly emphasis?: boolean;
}): ReactElement {
  return (
    <Stack gap="small" className="min-w-0">
      <span className="text-hurdl-caption font-hurdl-semibold uppercase tracking-wide text-hurdl-muted-foreground">
        {label}
      </span>
      {value === undefined ? (
        <Skeleton className="h-hurdl-8 w-12" />
      ) : (
        <span
          className={`font-hurdl-display text-hurdl-heading font-hurdl-semibold leading-hurdl-tight ${
            emphasis ? 'text-hurdl-primary-background' : 'text-card-foreground'
          }`}
        >
          {value}
        </span>
      )}
    </Stack>
  );
}
